'use client';

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAppStore } from '@/store/app';
import { useRequireAuth } from '@/lib/useRequireAuth';
import EventPing from '@/components/EventPing';
import { api } from '@/lib/api';
import ResearchTabs from '@/components/ResearchTabs';

/* ------------------------------ types ------------------------------ */

interface BookLevel { price: number; qty: number; }
interface OrderBookSnapshot {
  exchange: string;
  type: 'spot' | 'perp';
  symbol: string;
  eventTime: number;
  receivedAt: number;
  sequence?: string | number;
  bids: BookLevel[];
  asks: BookLevel[];
}
interface DepthBucket { bid: number; ask: number; total: number; bidCoverPct?: number | null; askCoverPct?: number | null; }
interface MarketMetrics {
  bestBid: number; bestAsk: number; mid: number; spread: number; spreadPct: number;
  depth0_5: DepthBucket; depth1: DepthBucket; depth2: DepthBucket;
}
interface SlippageEstimate {
  side: 'buy' | 'sell'; input: number; inputIsQuote: boolean;
  filledBase: number; filledQuote: number; filledRatio: number;
  avgPrice: number; costBps: number; partial: boolean;
}
interface DepthSeriesPoint { price: number; cum: number; }
interface DepthSeries { mid: number; bidSeries: DepthSeriesPoint[]; askSeries: DepthSeriesPoint[]; }
interface EstimateResult {
  snapshot: OrderBookSnapshot;
  metrics: MarketMetrics;
  slippage: SlippageEstimate;
  depthSeries: DepthSeries;
}
interface BasisRow {
  exchange: string;
  perpPrice: number | null;
  spotPrice: number | null;
  basis: number | null;
  eventTime: number;
  stale: boolean;
}
interface BasisResult {
  symbol: string;
  rows: BasisRow[];
  updatedAt: number;
  note: string;
}

const EXCHANGES = [
  { id: 'binance', label: 'Binance' },
  { id: 'okx', label: 'OKX' },
  { id: 'gate', label: 'Gate' },
  { id: 'bitget', label: 'Bitget' },
];

/* ------------------------------ helpers ------------------------------ */

function fmtNum(v: number | null | undefined, dp = 2): string {
  if (v === null || v === undefined || !isFinite(v)) return '—';
  return v.toLocaleString(undefined, { maximumFractionDigits: dp });
}
function fmtMoney(v: number | null | undefined): string {
  if (v === null || v === undefined || !isFinite(v)) return '—';
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
}
function fmtBps(v: number | null | undefined): string {
  if (v === null || v === undefined || !isFinite(v)) return '—';
  return `${v >= 0 ? '+' : ''}${(v * 100).toFixed(3)}%`;
}
function fmtBasis(v: number | null | undefined): string {
  if (v === null || v === undefined || !isFinite(v)) return '—';
  return `${v >= 0 ? '+' : ''}${(v * 100).toFixed(4)}%`;
}

/* ------------------------------ SVG 深度图（确定性阶梯） ------------------------------ */

function DepthChart({ series, zh }: { series: DepthSeries | null; zh: boolean }) {
  const W = 540;
  const H = 240;
  const padL = 52;
  const padR = 14;
  const padT = 14;
  const padB = 26;

  if (!series) {
    return (
      <div className="flex h-[240px] items-center justify-center rounded-lg border border-gf-border/50 bg-gf-surface2/50 text-xs text-gf-muted">
        无深度数据
      </div>
    );
  }

  const bidAsc = [...series.bidSeries].reverse(); // 低价 → best bid
  const ask = series.askSeries; // best ask → 高价
  const all = [...bidAsc, ...ask];
  if (all.length < 2) {
    return (
      <div className="flex h-[240px] items-center justify-center rounded-lg border border-gf-border/50 bg-gf-surface2/50 text-xs text-gf-muted">
        深度不足
      </div>
    );
  }
  const prices = all.map((p) => p.price);
  const minP = Math.min(...prices);
  const maxP = Math.max(...prices);
  const maxCum = Math.max(...all.map((p) => p.cum));
  const range = maxP - minP || 1;
  const x = (p: number) => padL + ((p - minP) / range) * (W - padL - padR);
  const y = (c: number) => H - padB - (c / (maxCum || 1)) * (H - padT - padB);

  const bidPath = (() => {
    let d = `M ${x(bidAsc[0]?.price ?? minP)} ${H - padB}`;
    for (const pt of bidAsc) {
      d += ` L ${x(pt.price)} ${H - padB} L ${x(pt.price)} ${y(pt.cum)}`;
    }
    const last = bidAsc[bidAsc.length - 1];
    if (last) d += ` L ${x(last.price)} ${y(last.cum)} L ${x(last.price)} ${H - padB} Z`;
    return d;
  })();
  const askPath = (() => {
    const first = ask[0];
    let d = `M ${first ? x(first.price) : x(maxP)} ${H - padB}`;
    for (const pt of ask) {
      d += ` L ${x(pt.price)} ${H - padB} L ${x(pt.price)} ${y(pt.cum)}`;
    }
    const last = ask[ask.length - 1];
    if (last) d += ` L ${x(last.price)} ${y(last.cum)} L ${x(last.price)} ${H - padB} Z`;
    return d;
  })();

  const midX = x(series.mid);
  const ticks = 5;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="order book depth chart">
      {/* 网格 */}
      {Array.from({ length: ticks + 1 }).map((_, i) => {
        const yy = padT + ((H - padT - padB) / ticks) * i;
        const cum = maxCum * (1 - i / ticks);
        return (
          <g key={i}>
            <line x1={padL} y1={yy} x2={W - padR} y2={yy} stroke="rgba(148,163,184,0.15)" strokeWidth={1} />
            <text x={padL - 6} y={yy + 3} textAnchor="end" fontSize={10} fill="#94a3b8">{fmtMoney(cum)}</text>
          </g>
        );
      })}
      {/* 深度曲线 */}
      <path d={bidPath} fill="rgba(16,185,129,0.22)" stroke="#10b981" strokeWidth={1.5} />
      <path d={askPath} fill="rgba(239,68,68,0.22)" stroke="#ef4444" strokeWidth={1.5} />
      {/* D08: 截断边缘标注 — 最远观测档位，不得把截断订单簿当完整深度 */}
      {bidAsc.length > 0 && (
        <>
          <line x1={x(bidAsc[0].price)} y1={padT} x2={x(bidAsc[0].price)} y2={H - padB} stroke="#f59e0b" strokeWidth={1} strokeDasharray="2 3" opacity={0.6} />
          <text x={x(bidAsc[0].price)} y={H - 8} textAnchor="middle" fontSize={9} fill="#f59e0b">{zh ? '截断' : 'cut'}</text>
        </>
      )}
      {ask.length > 0 && (
        <>
          <line x1={x(ask[ask.length - 1].price)} y1={padT} x2={x(ask[ask.length - 1].price)} y2={H - padB} stroke="#f59e0b" strokeWidth={1} strokeDasharray="2 3" opacity={0.6} />
          <text x={x(ask[ask.length - 1].price)} y={H - 8} textAnchor="middle" fontSize={9} fill="#f59e0b">{zh ? '截断' : 'cut'}</text>
        </>
      )}
      {/* mid 参考线 */}
      <line x1={midX} y1={padT} x2={midX} y2={H - padB} stroke="#f59e0b" strokeWidth={1} strokeDasharray="4 3" />
      <text x={midX} y={padT - 3} textAnchor="middle" fontSize={10} fill="#f59e0b">{fmtNum(series.mid)}</text>
      {/* 底部价格刻度 */}
      {[0, 0.25, 0.5, 0.75, 1].map((r) => {
        const px = minP + range * r;
        return (
          <text key={r} x={padL + r * (W - padL - padR)} y={H - 6} textAnchor="middle" fontSize={10} fill="#94a3b8">
            {fmtNum(px, 2)}
          </text>
        );
      })}
    </svg>
  );
}

/* ------------------------------ 摘要卡 ------------------------------ */

function MetricsCard({ m, zh }: { m: MarketMetrics | null; zh: boolean }) {
  const rows = m
    ? [
        [zh ? '中间价' : 'Mid', fmtNum(m.mid, 6)],
        [zh ? '买一 / 卖一' : 'Best bid / ask', `${fmtNum(m.bestBid, 6)} / ${fmtNum(m.bestAsk, 6)}`],
        [zh ? '价差' : 'Spread', `${fmtNum(m.spread, 6)} (${(m.spreadPct * 10000).toFixed(2)} bps)`],
      ]
    : [];
  const depthRows: Array<[string, DepthBucket, number]> = m
    ? [
        [zh ? '±0.5% 深度' : '±0.5% depth', m.depth0_5, 0.005],
        [zh ? '±1% 深度' : '±1% depth', m.depth1, 0.01],
        [zh ? '±2% 深度' : '±2% depth', m.depth2, 0.02],
      ]
    : [];
  // D08: 订单簿截断 → 价格带未被完整覆盖时，金额不能冒充完整深度
  const short = depthRows.filter(([, d, target]) => (d.bidCoverPct ?? 0) < target || (d.askCoverPct ?? 0) < target);
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2">
        {rows.map(([k, v], i) => (
          <div key={i} className="rounded-lg border border-gf-border/50 p-2">
            <div className="text-[11px] text-gf-muted">{k}</div>
            <div className="mt-0.5 break-words text-xs font-semibold leading-snug text-gf-text">{v}</div>
          </div>
        ))}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="border-b border-gf-border/50 text-gf-muted">
              <th className="py-1 text-left font-medium">{zh ? '价格带' : 'Band'}</th>
              <th className="py-1 text-right font-medium">{zh ? '买方金额' : 'Bid'}</th>
              <th className="py-1 text-right font-medium">{zh ? '卖方金额' : 'Ask'}</th>
              <th className="py-1 text-right font-medium">{zh ? '合计' : 'Total'}</th>
            </tr>
          </thead>
          <tbody>
            {depthRows.map(([k, d, target], i) => {
              const shortRow = (d.bidCoverPct ?? 0) < target || (d.askCoverPct ?? 0) < target;
              return (
                <tr key={i} className="border-b border-gf-border/30 text-gf-text">
                  <td className="py-1">
                    {k}
                    {shortRow && (
                      <span className="ml-1 rounded bg-amber-500/10 px-1 text-[10px] text-amber-500">
                        {zh ? `覆盖 ${Math.max(d.bidCoverPct ?? 0, d.askCoverPct ?? 0) * 100 >= 0.005 ? (Math.max(d.bidCoverPct ?? 0, d.askCoverPct ?? 0) * 100).toFixed(2) : '<0.01'}%` : `cov ${Math.max(d.bidCoverPct ?? 0, d.askCoverPct ?? 0) * 100 < 0.005 ? '<0.01' : (Math.max(d.bidCoverPct ?? 0, d.askCoverPct ?? 0) * 100).toFixed(2)}%`}
                      </span>
                    )}
                  </td>
                  <td className="py-1 text-right text-emerald-500">{fmtMoney(d.bid)}</td>
                  <td className="py-1 text-right text-red-500">{fmtMoney(d.ask)}</td>
                  <td className="py-1 text-right font-medium">{fmtMoney(d.total)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {short.length > 0 && (
          <div className="mt-1 text-[11px] text-amber-500">
            {zh
              ? `已观测深度，未覆盖完整价格带（订单簿截断，缺失远端不补零）`
              : 'Observed depth only — full band not covered (book truncated; missing far levels not zero-filled)'}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------ 页面 ------------------------------ */

export default function LiquidityPage() {
  const { language } = useAppStore();
  const authReady = useRequireAuth('/research/liquidity', true);
  const zh = language !== 'en';

  const t = useMemo(
    () => ({
      title: zh ? '流动性／基差研究' : 'Liquidity & Basis',
      subtitle: zh
        ? '盘口深度、滑点估算与永续基差 —— 静态估算，按「估算」计算，无执行能力'
        : 'Order book depth, slippage estimation and perp basis — static estimates, "Estimate" only, no execution',
      type: zh ? '类型' : 'Type',
      spot: zh ? '现货' : 'Spot',
      perp: zh ? '合约' : 'Perp',
      exchange: zh ? '交易所' : 'Exchange',
      symbol: zh ? '币对' : 'Symbol',
      refresh: zh ? '刷新盘口' : 'Refresh',
      depth: zh ? '盘口深度' : 'Order book depth',
      estimate: zh ? '估算' : 'Estimate',
      estimating: zh ? '估算中…' : 'Estimating…',
      amount: zh ? '数量（基础币）' : 'Amount (base)',
      notional: zh ? '金额（报价币）' : 'Notional (quote)',
      side: zh ? '方向' : 'Side',
      buy: zh ? '买入' : 'Buy',
      sell: zh ? '卖出' : 'Sell',
      unit: zh ? '输入单位' : 'Input unit',
      baseCoin: zh ? '基础币数量' : 'Base qty',
      quoteCoin: zh ? '报价币金额' : 'Quote value',
      result: zh ? '估算结果' : 'Result',
      filled: zh ? '可成交' : 'Filled',
      ratio: zh ? '成交占比' : 'Fill ratio',
      avgPrice: zh ? 'VWAP 均价' : 'VWAP',
      cost: zh ? '成本偏差' : 'Cost vs mid',
      partial: zh ? '深度不足：仅部分可成交，剩余未假定以最后价成交' : 'Insufficient depth: partial fill only, remainder not assumed at last price',
      basis: zh ? '永续基差（4 交易所）' : 'Perp basis (4 venues)',
      basisSub: zh ? '永续溢价 = 永续参考价 / 现货参考价 − 1（同一时点、同报价币）' : 'Premium = perp ref / spot ref − 1 (same moment, same quote)',
      perpRef: zh ? '永续价' : 'Perp',
      spotRef: zh ? '现货价' : 'Spot',
      premium: zh ? '溢价' : 'Premium',
      stale: zh ? '参考价缺失' : 'Ref missing',
      updated: zh ? '更新于' : 'Updated',
      err: zh ? '请求失败' : 'Request failed',
      loading: zh ? '载入中…' : 'Loading…',
      noData: zh ? '暂无数据' : 'No data',
      disclaimer: zh
        ? '估算不含执行成本、保证金、借贷费用及市场冲击以外的风险；不构成套利保证。盘口为实时 REST 快照（不落库），仅在档位连续、序列正确时展示。'
        : 'Estimate excludes execution costs, margin, borrowing fees and risks beyond market impact; not an arbitrage guarantee. Snapshot is a live REST order book (not persisted), shown only when levels are contiguous and sequence is valid.',
    }),
    [zh],
  );

  const [type, setType] = useState<'spot' | 'perp'>('perp');
  const [exchange, setExchange] = useState('binance');
  const [symbol, setSymbol] = useState('BTCUSDT');
  const [input, setInput] = useState('1');
  const [side, setSide] = useState<'buy' | 'sell'>('buy');
  const [inputIsQuote, setInputIsQuote] = useState(false);
  const [estReq, setEstReq] = useState<{ nonce: number } | null>(null);

  const queryKey = ['liquidity-orderbook', type, exchange, symbol.toUpperCase()];
  const obQ = useQuery({
    queryKey,
    queryFn: () => api.get<OrderBookSnapshot & { metrics: MarketMetrics }>(`/liquidity/orderbook?type=${type}&exchange=${exchange}&symbol=${encodeURIComponent(symbol.toUpperCase())}`),
    refetchInterval: 15_000,
    retry: 1,
  });

  const basisQ = useQuery({
    queryKey: ['liquidity-basis', symbol.toUpperCase()],
    queryFn: () => api.get<BasisResult>(`/liquidity/basis?symbol=${encodeURIComponent(symbol.toUpperCase())}`),
    refetchInterval: 30_000,
    retry: 1,
  });

  const estQ = useQuery({
    queryKey: ['liquidity-estimate', type, exchange, symbol.toUpperCase(), input, side, inputIsQuote, estReq?.nonce ?? 0],
    queryFn: () =>
      api.get<EstimateResult>(
        `/liquidity/estimate?type=${type}&exchange=${exchange}&symbol=${encodeURIComponent(symbol.toUpperCase())}&input=${encodeURIComponent(input)}&side=${side}&inputIsQuote=${inputIsQuote}`,
      ),
    enabled: estReq !== null && parseFloat(input) > 0,
    retry: 1,
  });

  const metrics = obQ.data?.metrics ?? null;
  const depthSeries: DepthSeries | null = useMemo(() => {
    if (!obQ.data) return null;
    return buildClientSeries(obQ.data);
  }, [obQ.data]);

  const est = estQ.data;

  
  // ---------- 渲染 ----------
  return (
    <div className="mx-auto w-full max-w-6xl px-4 pb-24 pt-6">
      <EventPing event="liquidity_view" />
      <ResearchTabs />
      <div className="mb-4">
        <h1 className="text-xl font-semibold text-gf-text">{t.title}</h1>
        <p className="mt-1 text-xs text-gf-muted">{t.subtitle}</p>
      </div>

      {/* ---------- 市场选择（双端一致，顶部） ---------- */}
      <div className="card mb-4 p-4">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <label className="space-y-1">
            <span className="text-xs text-gf-muted">{t.type}</span>
            <select value={type} onChange={(e) => setType(e.target.value as 'spot' | 'perp')} className="input !h-9 text-xs">
              <option value="perp">{t.perp}</option>
              <option value="spot">{t.spot}</option>
            </select>
          </label>
          <label className="space-y-1">
            <span className="text-xs text-gf-muted">{t.exchange}</span>
            <select value={exchange} onChange={(e) => setExchange(e.target.value)} className="input !h-9 text-xs">
              {EXCHANGES.map((ex) => <option key={ex.id} value={ex.id}>{ex.label}</option>)}
            </select>
          </label>
          <label className="space-y-1">
            <span className="text-xs text-gf-muted">{t.symbol}</span>
            <input value={symbol} onChange={(e) => setSymbol(e.target.value.toUpperCase())} className="input !h-9 text-xs" placeholder="BTCUSDT" />
          </label>
          <div className="flex items-end">
            <button onClick={() => { obQ.refetch(); }} className="btn-outline w-full !h-9 text-xs">
              {t.refresh}
            </button>
          </div>
        </div>
        {obQ.isError && (
          <div className="mt-2 text-xs text-red-500">{t.err}: {String((obQ.error as Error)?.message ?? obQ.error)}</div>
        )}
      </div>

      {/* ---------- 桌面：左图右表单 ---------- */}
      <div className="grid gap-4 lg:grid-cols-[1fr_340px]">
        {/* 左列：深度图 + 摘要 + 基差（桌面）；手机顺序：摘要 → 图 → 基差 */}
        <div className="space-y-4">
          {/* 摘要 */}
          <div className="card p-4">
            <div className="mb-2 text-sm font-medium text-gf-text">{zh ? '盘口摘要' : 'Book summary'}</div>
            {obQ.isLoading ? (
              <div className="text-xs text-gf-muted">{t.loading}</div>
            ) : metrics ? (
              <MetricsCard m={metrics} zh={zh} />
            ) : (
              <div className="text-xs text-gf-muted">{t.noData}</div>
            )}
          </div>

          {/* 深度图 */}
          <div className="card p-4">
            <div className="mb-2 text-sm font-medium text-gf-text">{t.depth}</div>
            {obQ.isLoading ? (
              <div className="text-xs text-gf-muted">{t.loading}</div>
            ) : (
              <DepthChart series={depthSeries} zh={zh} />
            )}
            {obQ.data && (
              <div className="mt-1 flex flex-wrap gap-x-4 text-[11px] text-gf-muted">
                <span>{zh ? '交易所' : 'Venue'}: {obQ.data.exchange} · {obQ.data.type}</span>
                <span>seq: {String(obQ.data.sequence ?? '—')}</span>
                <span>Δ{Math.max(0, Math.round((Date.now() - obQ.data.receivedAt) / 1000))}s</span>
              </div>
            )}
          </div>

          {/* 基差 */}
          <div className="card p-4">
            <div className="text-sm font-medium text-gf-text">{t.basis}</div>
            <div className="mb-2 text-[11px] text-gf-muted">{t.basisSub}</div>
            {basisQ.isLoading ? (
              <div className="text-xs text-gf-muted">{t.loading}</div>
            ) : basisQ.data ? (
              <>
                <div className="space-y-2">
                  {basisQ.data.rows.map((r) => {
                    const up = r.basis !== null && r.basis >= 0;
                    const pct = r.basis !== null ? Math.min(100, Math.abs(r.basis) * 5000) : 0;
                    return (
                      <div key={r.exchange} className="rounded-lg border border-gf-border/50 p-2">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-medium capitalize text-gf-text">{r.exchange}</span>
                          <span className={`font-semibold ${r.stale ? 'text-gf-muted' : up ? 'text-emerald-500' : 'text-red-500'}`}>
                            {r.stale ? t.stale : fmtBasis(r.basis)}
                          </span>
                        </div>
                        <div className="mt-1 flex items-center gap-2 text-[11px] text-gf-muted">
                          <span>{t.perpRef}: {fmtNum(r.perpPrice, 4)}</span>
                          <span>{t.spotRef}: {fmtNum(r.spotPrice, 4)}</span>
                        </div>
                        {!r.stale && (
                          <div className="mt-1 h-1 overflow-hidden rounded bg-gf-surface2">
                            <div
                              className={`h-full rounded ${up ? 'bg-emerald-500' : 'bg-red-500'}`}
                              style={{ width: `${pct}%`, marginLeft: up ? '0%' : `${100 - pct}%` }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-2 text-[11px] text-gf-muted">
                  {t.updated}: {new Date(basisQ.data.updatedAt).toLocaleTimeString()}
                </div>
              </>
            ) : (
              <div className="text-xs text-gf-muted">{t.noData}</div>
            )}
          </div>
        </div>

        {/* 右列：估算表单 + 结果（桌面）；手机在图表之后 */}
        <div className="space-y-4">
          <div className="card sticky top-16 space-y-3 p-4">
            <div className="text-sm font-medium text-gf-text">{t.estimate}</div>
            <div className="grid grid-cols-2 gap-2">
              <label className="space-y-1">
                <span className="text-xs text-gf-muted">{t.side}</span>
                <select value={side} onChange={(e) => setSide(e.target.value as 'buy' | 'sell')} className="input !h-9 text-xs">
                  <option value="buy">{t.buy}</option>
                  <option value="sell">{t.sell}</option>
                </select>
              </label>
              <label className="space-y-1">
                <span className="text-xs text-gf-muted">{t.unit}</span>
                <select value={inputIsQuote ? 'quote' : 'base'} onChange={(e) => setInputIsQuote(e.target.value === 'quote')} className="input !h-9 text-xs">
                  <option value="base">{t.baseCoin}</option>
                  <option value="quote">{t.quoteCoin}</option>
                </select>
              </label>
            </div>
            <label className="space-y-1 block">
              <span className="text-xs text-gf-muted">{inputIsQuote ? t.notional : t.amount}</span>
              <input type="number" min={0} step="any" value={input} onChange={(e) => setInput(e.target.value)} className="input !h-9 text-xs" />
            </label>
            <button
              onClick={() => setEstReq({ nonce: Date.now() })}
              disabled={!(parseFloat(input) > 0) || estQ.isFetching}
              className="btn-primary w-full !h-10"
            >
              {estQ.isFetching ? t.estimating : t.estimate}
            </button>

            {/* 结果 */}
            {est && (
              <div className="space-y-2 rounded-lg border border-gf-border/50 p-3">
                <div className="text-xs font-medium text-gf-text">{t.result}</div>
                {/* D08: 估算绑定快照 — 显示该快照的最佳买卖价与时间，避免与另一份轮询快照混淆 */}
                <div className="flex flex-wrap items-center justify-between gap-x-2 text-[11px] text-gf-muted">
                  <span>
                    {zh ? '快照' : 'Snapshot'} @ {new Date(est.snapshot.receivedAt).toLocaleTimeString()}
                    {est.snapshot.sequence !== undefined && ` · seq ${String(est.snapshot.sequence).slice(0, 12)}`}
                  </span>
                  <span className="font-mono">
                    {zh ? '买一' : 'bid'} {fmtNum(est.snapshot.bids[0]?.price ?? 0, 6)} · {zh ? '卖一' : 'ask'} {fmtNum(est.snapshot.asks[0]?.price ?? 0, 6)}
                  </span>
                </div>
                {obQ.data && obQ.data.receivedAt > est.snapshot.receivedAt && (
                  <div className="rounded bg-amber-500/10 px-2 py-1.5 text-[11px] text-amber-500">
                    {zh ? '盘口已更新，此估算基于旧快照 — 建议重新估算' : 'Book updated; estimate uses an older snapshot — re-estimate'}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2 text-center">
                  <div className="rounded bg-gf-surface2 p-2">
                    <div className="text-[11px] text-gf-muted">{t.filled} ({zh ? '基础币' : 'base'})</div>
                    <div className="text-sm font-semibold text-gf-text">{fmtNum(est.slippage.filledBase, 6)}</div>
                  </div>
                  <div className="rounded bg-gf-surface2 p-2">
                    <div className="text-[11px] text-gf-muted">{t.filled} (USDT)</div>
                    <div className="text-sm font-semibold text-gf-text">{fmtMoney(est.slippage.filledQuote)}</div>
                  </div>
                  <div className="rounded bg-gf-surface2 p-2">
                    <div className="text-[11px] text-gf-muted">{t.avgPrice}</div>
                    <div className="text-sm font-semibold text-gf-text">{fmtNum(est.slippage.avgPrice, 6)}</div>
                  </div>
                  <div className="rounded bg-gf-surface2 p-2">
                    <div className="text-[11px] text-gf-muted">{t.cost}</div>
                    <div className={`text-sm font-semibold ${est.slippage.costBps >= 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                      {fmtBps(est.slippage.costBps)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-gf-muted">{t.ratio}</span>
                  <span className={est.slippage.partial ? 'font-medium text-amber-500' : 'font-medium text-emerald-500'}>
                    {Math.round(est.slippage.filledRatio * 100)}%
                  </span>
                </div>
                {est.slippage.partial && (
                  <div className="rounded bg-amber-500/10 px-2 py-1.5 text-[11px] text-amber-500">{t.partial}</div>
                )}
              </div>
            )}
            {estQ.isError && !est && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-500">
                {t.err}: {String((estQ.error as Error)?.message ?? '')}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-gf-border/50 bg-gf-surface2/50 p-3 text-[11px] text-gf-muted">
        {t.disclaimer}
      </div>
    </div>
  );
}

/* 客户端由归一化快照构建深度系列（与后端 buildDepthSeries 同一逻辑）。 */
function buildClientSeries(ob: { bids: BookLevel[]; asks: BookLevel[] }): DepthSeries | null {
  const bestBid = ob.bids[0]?.price ?? 0;
  const bestAsk = ob.asks[0]?.price ?? 0;
  const mid = (bestAsk + bestBid) / 2 || 0;
  const maxPct = 0.02;

  const bidSeries: DepthSeriesPoint[] = [];
  const askSeries: DepthSeriesPoint[] = [];
  let cumBid = 0;
  let cumAsk = 0;
  for (const l of ob.bids) {
    if (mid > 0 && l.price >= mid * (1 - maxPct)) {
      cumBid += l.price * l.qty;
      bidSeries.push({ price: l.price, cum: cumBid });
    }
  }
  for (const l of ob.asks) {
    if (mid > 0 && l.price <= mid * (1 + maxPct)) {
      cumAsk += l.price * l.qty;
      askSeries.push({ price: l.price, cum: cumAsk });
    }
  }
  return { mid, bidSeries, askSeries };
}
